/**
 * Service de gestion des snapshots quotidiens
 * Permet de créer/mettre à jour les snapshots pour le graphique d'évolution
 */

import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface SnapshotData {
  chantierId: string;
  budgetHt?: number;
  coutMainOeuvre?: number;
  coutsFixes?: number;
  margeADate?: number;
  profitabilityPct?: number;
}

/**
 * Crée ou met à jour le snapshot du jour pour un chantier
 * Utilise UPSERT pour éviter les doublons (basé sur chantier_id + date)
 */
export async function upsertTodaySnapshot(data: SnapshotData): Promise<boolean> {
  const today = format(new Date(), 'yyyy-MM-dd');

  try {
    // Vérifier si un snapshot existe déjà pour aujourd'hui
    const { data: existingSnapshot } = await supabase
      .from('chantier_snapshots')
      .select('id')
      .eq('chantier_id', data.chantierId)
      .eq('d', today)
      .maybeSingle();

    if (existingSnapshot) {
      // Mettre à jour le snapshot existant
      const { error } = await supabase
        .from('chantier_snapshots')
        .update({
          budget_ht: data.budgetHt,
          cout_main_oeuvre: data.coutMainOeuvre,
          couts_fixes: data.coutsFixes,
          marge_a_date: data.margeADate,
          profitability_pct: data.profitabilityPct,
        })
        .eq('id', existingSnapshot.id);

      if (error) throw error;
      if (import.meta.env.DEV) console.log('[snapshotService] Snapshot mis à jour pour', data.chantierId);
    } else {
      // Créer un nouveau snapshot
      const { error } = await supabase
        .from('chantier_snapshots')
        .insert({
          chantier_id: data.chantierId,
          d: today,
          budget_ht: data.budgetHt,
          cout_main_oeuvre: data.coutMainOeuvre,
          couts_fixes: data.coutsFixes,
          marge_a_date: data.margeADate,
          profitability_pct: data.profitabilityPct,
        });

      if (error) throw error;
      if (import.meta.env.DEV) console.log('[snapshotService] Nouveau snapshot créé pour', data.chantierId);
    }

    return true;
  } catch (error) {
    console.error('[snapshotService] Erreur création/mise à jour snapshot:', error);
    return false;
  }
}

/**
 * Crée un snapshot à partir des métriques temps réel d'un chantier
 * Récupère automatiquement les métriques depuis chantier_metrics_realtime
 */
export async function createSnapshotFromMetrics(chantierId: string): Promise<boolean> {
  try {
    // Récupérer les métriques actuelles
    const { data: metricsData, error: metricsError } = await supabase
      .from('chantier_metrics_realtime')
      .select('metrics')
      .eq('chantier_id', chantierId)
      .maybeSingle();

    if (metricsError) throw metricsError;

    if (!metricsData?.metrics) {
      if (import.meta.env.DEV) console.log('[snapshotService] Pas de métriques pour', chantierId);
      return false;
    }

    const metrics = metricsData.metrics as any;

    // Créer le snapshot avec les données des métriques
    return await upsertTodaySnapshot({
      chantierId,
      budgetHt: metrics.budget_ht || 0,
      coutMainOeuvre: metrics.cout_main_oeuvre_reel || 0,
      coutsFixes: metrics.couts_fixes_engages || 0,
      margeADate: metrics.marge_a_date || 0,
      profitabilityPct: metrics.profitability_pct || 0,
    });
  } catch (error) {
    console.error('[snapshotService] Erreur création snapshot depuis métriques:', error);
    return false;
  }
}
